// src/pages/Admin/AdminProperties.js
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Home as HomeIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import { adminAPI } from '../../services/api'
import { API_BASE_URL } from '../../config'

const STATUS_LABELS = {
  approved: { label: 'Aprovado', color: 'success' },
  pending: { label: 'Em avaliação', color: 'warning' },
  rejected: { label: 'Rejeitado', color: 'error' },
}

const AdminProperties = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [properties, setProperties] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [deleteDialog, setDeleteDialog] = useState({ open: false, property: null })
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (user && !user.is_staff) {
      navigate('/')
    }
  }, [user, navigate])

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const params = { page: page + 1 }
      if (search) params.search = search
      if (statusFilter) params.status = statusFilter
      const response = await adminAPI.getAllProperties(params)
      setProperties(response.data.results || [])
      setTotal(response.data.count || 0)
    } catch {
      setError('Erro ao carregar imóveis.')
    } finally {
      setLoading(false)
    }
  }, [page, search, statusFilter])

  useEffect(() => {
    fetchProperties()
  }, [fetchProperties])

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setPage(0)
  }

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value)
    setPage(0)
  }

  const handleDelete = async () => {
    if (!deleteDialog.property) return
    try {
      setDeleting(true)
      await adminAPI.deleteProperty(deleteDialog.property.id)
      setDeleteDialog({ open: false, property: null })
      fetchProperties()
    } catch {
      setError('Erro ao excluir imóvel.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <HomeIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          Gerenciar Imóveis
        </Typography>
        <Chip label={total} color="primary" size="small" sx={{ ml: 1 }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar por título, dono ou cidade..."
          value={search}
          onChange={handleSearchChange}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={handleStatusChange} label="Status">
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="approved">Aprovado</MenuItem>
            <MenuItem value="pending">Em avaliação</MenuItem>
            <MenuItem value="rejected">Rejeitado</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Imóvel</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Dono</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cidade</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cadastrado em</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : properties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nenhum imóvel encontrado.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                properties.map((property) => {
                  const statusInfo = STATUS_LABELS[property.approval_status] || { label: property.approval_status, color: 'default' }
                  const mainImage = property.images?.find((img) => img.is_main)?.image || property.images?.[0]?.image
                  return (
                    <TableRow key={property.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            variant="rounded"
                            src={mainImage ? `${API_BASE_URL}${mainImage}` : undefined}
                            sx={{ width: 48, height: 48 }}
                          >
                            <HomeIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                              {property.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              #{property.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{property.owner_name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {property.property_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{property.city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(property.created_at).toLocaleDateString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => setDeleteDialog({ open: true, property })}
                        >
                          Excluir
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={20}
          rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, property: null })}
        maxWidth="xs"
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja excluir <strong>"{deleteDialog.property?.title}"</strong>?
            Esta ação não pode ser desfeita e remove todos os dados associados (imagens, impulsionamentos, pagamentos).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, property: null })}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AdminProperties
